// Copyright (c) 2020 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import * as Actions from "features/timeline/actions"
import * as TimelineSlice from "features/timeline/slices/timeline"
import * as TimelineActions from "../actions"
import { RemoveFromTimeGroupRequest, TimeGroup, } from "../../../generated/wisetime/user/timeline/v4/timeline_pb"
import { grpc } from "app/rpc/executor"
import { timelineClient } from "app/rpc/client"
import { isGrpcError } from "../../../lib/rpcutils"
import { StatusCode } from "grpc-web"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"

type RemoveFromTimeGroupRpc = (request: RemoveFromTimeGroupRequest) => Observable<TimeGroup>

export const removeFromTimeGroup = (removeFromTimeGroupRpc: RemoveFromTimeGroupRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.removeFromTimeGroup.match),
      mergeMap(({ payload: request }) =>
        of(request)
          .pipe(
            mergeMap(removeFromTimeGroupRpc),
            map(timeGroup => TimelineSlice.removeFromTimeGroup({ timeGroup, request })),
            catchError(handleError),
          )),
    )

const removeFromTimeGroupRpc = (request: RemoveFromTimeGroupRequest): Observable<TimeGroup> =>
  grpc(() => timelineClient.removeFromTimeGroup(request, null))

const handleError = (e: unknown): Observable<Action> => {
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.NOT_FOUND) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_remove_from_time_group_not_found", message: "Time group or time row(s) not found." }),
          messageType: "ERROR",
        })),
        of(TimelineActions.onTimelineOutOfSync()),
      )
    }
    if (code === StatusCode.PERMISSION_DENIED) {
      return of(showSnackBar({
        message: t({ id: "msg_remove_from_time_group_permission_denied", message: "Failed to remove time row(s) from the group. You do not have permission to update the time group." }),
        messageType: "ERROR",
      }))
    }
    if (code === StatusCode.FAILED_PRECONDITION) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_remove_from_time_group_failed_precondition", message: "Failed to remove time row(s) from the group. Time row(s) or group may be out of sync." }),
          messageType: "ERROR",
        })),
        of(TimelineActions.onTimelineOutOfSync()),
      )
    }
  }

  return of(showSnackBar({
    message: t({ id: "msg_remove_from_time_group_failed", message: "Failed to remove time row(s) from the group. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}

export default removeFromTimeGroup(removeFromTimeGroupRpc)
